import { usePlayPickems } from '@/api/query/usePickem';

type PicksProps = {};

function getErrorMessage(err: unknown) {
  if (err instanceof Error) return err.message;
  // axios-style / api client errors often look like this:
  const anyErr = err as any;
  return (
    anyErr?.response?.data?.error ??
    anyErr?.response?.data?.message ??
    anyErr?.message ??
    'Something went wrong'
  );
}

function normalisePickems(result: any): any[] {
  // Common shapes:
  // - fetch: result = { pickems: [...] }
  // - axios: result = { data: { pickems: [...] } }
  // - your apiClient may already return body directly
  return (
    result?.pickems ?? result?.data?.pickems ?? result?.data?.data?.pickems ?? result?.data ?? []
  );
}

const Picks = () => {
  const { data, isLoading, isError, error, refetch, isFetching } = usePlayPickems();

  if (isLoading) {
    return (
      <div>
        <h2>My Picks</h2>
        <div>Loading…</div>
        {/* swap this for your skeleton component if you have one */}
      </div>
    );
  }

  if (isError) {
    return (
      <div>
        <h2>My Picks</h2>
        <div style={{ marginTop: 8 }}>
          <div style={{ marginBottom: 8 }}>Could not load picks: {getErrorMessage(error)}</div>
          <button onClick={() => refetch()}>Try again</button>
        </div>
      </div>
    );
  }

  const pickems = normalisePickems(data);

  return (
    <div>
      <h2>My Picks</h2>

      {isFetching ? <div style={{ marginTop: 8 }}>Refreshing…</div> : null}

      {pickems.length === 0 ? (
        <div style={{ marginTop: 8 }}>No pickems available.</div>
      ) : (
        <div style={{ marginTop: 12, display: 'grid', gap: 16 }}>
          {pickems.map((pickem: any) => {
            const selections = Array.isArray(pickem?.selections) ? pickem.selections : [];

            return (
              <div
                key={pickem.pickem_id}
                style={{ padding: 12, border: '1px solid #ddd', borderRadius: 8 }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>
                      {pickem.title_en ?? pickem.title ?? `Pickem ${pickem.pickem_id}`}
                    </div>
                    <div style={{ opacity: 0.8, fontSize: 12 }}>
                      Status: {pickem.status} • Entries: {pickem.entries ?? 0}
                    </div>
                  </div>

                  {pickem.has_entered ? (
                    <div style={{ fontSize: 12, opacity: 0.9 }}>Entered</div>
                  ) : (
                    <div style={{ fontSize: 12, opacity: 0.6 }}>Not entered</div>
                  )}
                </div>

                <div style={{ marginTop: 12 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Selections</div>

                  {!pickem.has_entered || selections.length === 0 ? (
                    <div style={{ opacity: 0.8 }}>No selections yet.</div>
                  ) : (
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {selections.map((sel: any) => {
                        // Your selections are enriched with points + correctness (getSelectionsPoints)
                        const points = sel.points ?? 0;

                        // Optional: map selection to question/option labels if questions are present
                        const question = pickem?.questions?.find?.(
                          (q: any) => String(q.question_id) === String(sel.question_id)
                        );
                        const option = question?.options?.find?.(
                          (o: any) => String(o.option_id) === String(sel.option_id)
                        );

                        const questionLabel =
                          question?.question_en ??
                          question?.question ??
                          question?.title ??
                          `Question ${sel.question_id}`;

                        const optionLabel =
                          option?.option_en ??
                          option?.option ??
                          option?.title ??
                          `Option ${sel.option_id}`;

                        const correctness =
                          sel.selection_is_correct === true
                            ? '✅'
                            : sel.selection_is_correct === false
                              ? '❌'
                              : '';

                        return (
                          <li key={`${sel.question_id}:${sel.option_id}`}>
                            <div>
                              <span style={{ fontWeight: 600 }}>{questionLabel}</span>
                              {' — '}
                              <span>{optionLabel}</span> <span>{correctness}</span>
                            </div>
                            <div style={{ fontSize: 12, opacity: 0.8 }}>
                              Points: {points}
                              {typeof sel.custom_value !== 'undefined' && sel.custom_value !== null
                                ? ` • Custom: ${sel.custom_value}`
                                : ''}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Picks;
